import enLocale from '@/locales/en.json'
import frLocale from '@/locales/fr.json'
import itLocale from '@/locales/it.json'
import { useAuthStore } from '@/stores/auth'
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// Simple debounce helper
function createDebounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

// Types
export interface TranslationKey {
  id: number
  keyPath: string
  description: string
  context: string
}

export interface Translation {
  id: number
  translationKeyId: number
  languageId: number
  languageCode: string
  value: string
}

export interface Language {
  code: string
  name: string
  flag: string
}

export interface Message {
  type: 'success' | 'error'
  text: string
}

export interface SyncStats {
  frontendKeys: number
  backendKeys: number
  missingKeys: number
}

export interface ComparisonResult {
  identical: Array<{ keyPath: string; value: string }>
  different: Array<{ keyPath: string; currentValue: string; newValue: string }>
  new: Array<{ keyPath: string; value: string }>
}

export function useTranslationManager() {
  const { t } = useI18n()
  const authStore = useAuthStore()

  // État
  const loading = ref(false)
  const exportLoading = ref(false)
  const syncLoading = ref(false)
  const analyzeLoading = ref(false)
  const saveAllLoading = ref(false)
  const importLoading = ref(false)
  const translationKeys = ref<TranslationKey[]>([])
  const translations = ref<Translation[]>([])
  const expandedKeys = ref<Set<number>>(new Set())
  const message = ref<Message | null>(null)
  const syncStats = ref<SyncStats | null>(null)

  // Batch sauvegardes manuelles
  const pendingChanges = ref<Map<string, { keyId: number; languageCode: string; value: string }>>(
    new Map()
  )

  // Import depuis JSON
  const showImportModal = ref(false)
  const importedData = ref<Record<string, string> | null>(null)
  const importLanguageCode = ref('')
  const selectedForImport = ref<Set<string>>(new Set())
  const comparisonResults = ref<ComparisonResult | null>(null)

  // Filtres
  const searchQuery = ref('')
  const debouncedSearchQuery = ref('')
  const selectedLanguage = ref('')
  const selectedContext = ref('')

  // Debounce pour la recherche
  const updateDebouncedSearch = createDebounce((query: string) => {
    debouncedSearchQuery.value = query
  }, 300)

  // Pagination
  const currentPage = ref(1)
  const itemsPerPage = 10

  // Valeurs éditables
  const editableValues = reactive<Record<string, string>>({})

  // Langues disponibles
  const availableLanguages = ref<Language[]>([
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  ])

  // Computed
  const availableContexts = computed(() => {
    const contexts = new Set(translationKeys.value.map(key => key.context))
    return Array.from(contexts).sort()
  })

  const filteredKeys = computed(() => {
    let filtered = translationKeys.value

    if (debouncedSearchQuery.value) {
      const query = debouncedSearchQuery.value.toLowerCase()
      filtered = filtered.filter(key => {
        // Recherche dans keyPath et description
        if (
          key.keyPath.toLowerCase().includes(query) ||
          key.description.toLowerCase().includes(query)
        ) {
          return true
        }

        // Recherche dans les valeurs traduites
        const keyTranslations = translations.value.filter(t => t.translationKeyId === key.id)
        return keyTranslations.some(t => t.value.toLowerCase().includes(query))
      })
    }

    if (selectedContext.value) {
      filtered = filtered.filter(key => key.context === selectedContext.value)
    }

    return filtered
  })

  const totalPages = computed(() => Math.ceil(filteredKeys.value.length / itemsPerPage))

  const paginatedKeys = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage
    const end = start + itemsPerPage
    return filteredKeys.value.slice(start, end)
  })

  const pendingChangesCount = computed(() => pendingChanges.value.size)

  const selectedDifferentCount = computed(() => {
    if (!comparisonResults.value) return 0
    return comparisonResults.value.different.filter(item =>
      selectedForImport.value.has(item.keyPath)
    ).length
  })

  const selectedNewCount = computed(() => {
    if (!comparisonResults.value) return 0
    return comparisonResults.value.new.filter(item => selectedForImport.value.has(item.keyPath))
      .length
  })

  // Helpers
  function getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (authStore.accessToken) {
      headers['Authorization'] = `Bearer ${authStore.accessToken}`
    }

    return headers
  }

  function getTranslation(keyId: number, languageCode: string): Translation | undefined {
    return translations.value.find(
      t => t.translationKeyId === keyId && t.languageCode === languageCode
    )
  }

  function showMessage(type: 'success' | 'error', text: string) {
    message.value = { type, text }
    setTimeout(() => {
      message.value = null
    }, 5000)
  }

  function loadFrontendLocales(): Record<string, Record<string, string>> {
    const locales: Record<string, Record<string, string>> = {}

    const localeFiles = {
      fr: frLocale,
      en: enLocale,
      it: itLocale,
    }

    for (const lang of availableLanguages.value) {
      const localeData = localeFiles[lang.code as keyof typeof localeFiles]
      if (localeData) {
        locales[lang.code] = {}
        Object.entries(localeData).forEach(([key, value]) => {
          if (typeof value === 'string') {
            locales[lang.code][key] = value
          }
        })
      }
    }

    return locales
  }

  // API Methods
  async function loadTranslations() {
    loading.value = true
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'

      const keysResponse = await fetch(`${API_BASE_URL}/translations/keys`)
      const keysData = await keysResponse.json()

      if (keysData.success) {
        translationKeys.value = keysData.data.map(
          (key: { id: number; keyPath: string; description: string; context: string }) => ({
            id: key.id,
            keyPath: key.keyPath,
            description: key.description,
            context: key.context,
          })
        )
      }

      const translationsResponse = await fetch(`${API_BASE_URL}/translations/all`)
      const translationsData = await translationsResponse.json()

      if (translationsData.success) {
        translations.value = translationsData.data.map(
          (t: {
            id: number
            translation_key_id: number
            language_id: number
            language_code: string
            value: string
          }) => ({
            id: t.id,
            translationKeyId: t.translation_key_id,
            languageId: t.language_id,
            languageCode: t.language_code,
            value: t.value,
          })
        )

        Object.keys(editableValues).forEach(key => delete editableValues[key])
        translations.value.forEach(t => {
          const key = `${t.translationKeyId}-${t.languageCode}`
          editableValues[key] = t.value
        })
      }
    } catch (error) {
      console.error('[TranslationManager] Erreur chargement:', error)
      showMessage('error', t('translation.loading.error'))
    } finally {
      loading.value = false
    }
  }

  function updateTranslation(keyId: number, languageCode: string, value: string) {
    const changeKey = `${keyId}-${languageCode}`
    editableValues[changeKey] = value

    const currentTranslation = getTranslation(keyId, languageCode)
    if (!currentTranslation || currentTranslation.value !== value) {
      pendingChanges.value.set(changeKey, { keyId, languageCode, value })
    } else {
      pendingChanges.value.delete(changeKey)
    }
  }

  function hasUnsavedChanges(keyId: number, languageCode: string): boolean {
    const changeKey = `${keyId}-${languageCode}`
    const currentTranslation = getTranslation(keyId, languageCode)
    const editableValue = editableValues[changeKey]

    if (!currentTranslation && editableValue) return true
    if (currentTranslation && editableValue !== currentTranslation.value) return true

    return false
  }

  async function saveTranslation(keyId: number, languageCode: string) {
    const changeKey = `${keyId}-${languageCode}`
    const newValue = editableValues[changeKey]

    if (!newValue) return

    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'

      const response = await fetch(`${API_BASE_URL}/translations/admin`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          translationKeyId: keyId,
          languageCode,
          value: newValue,
        }),
      })

      const data = await response.json()

      if (data.success) {
        const existingIndex = translations.value.findIndex(
          t => t.translationKeyId === keyId && t.languageCode === languageCode
        )

        if (existingIndex >= 0) {
          translations.value[existingIndex].value = newValue
        } else {
          translations.value.push({
            id: data.data.id,
            translationKeyId: keyId,
            languageId: data.data.language_id,
            languageCode,
            value: newValue,
          })
        }

        pendingChanges.value.delete(changeKey)
        showMessage('success', t('translation.save.success'))
      } else {
        showMessage('error', data.error || t('translation.save.error'))
      }
    } catch (error) {
      console.error('[TranslationManager] Erreur sauvegarde:', error)
      showMessage('error', t('translation.save.error'))
    }
  }

  async function saveAllPendingTranslations() {
    if (pendingChanges.value.size === 0) return

    saveAllLoading.value = true
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'

      const batchTranslations = Array.from(pendingChanges.value.values()).map(change => {
        const key = translationKeys.value.find(k => k.id === change.keyId)
        if (!key) {
          throw new Error(t('translation.batch.keyNotFound', { id: change.keyId }))
        }
        return {
          keyPath: key.keyPath,
          languageCode: change.languageCode,
          value: change.value,
        }
      })

      const BATCH_SIZE = 100
      let totalCreated = 0
      let totalUpdated = 0

      for (let i = 0; i < batchTranslations.length; i += BATCH_SIZE) {
        const batch = batchTranslations.slice(i, i + BATCH_SIZE)

        const response = await fetch(`${API_BASE_URL}/translations/batch`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ translations: batch }),
        })

        const data = await response.json()

        if (data.success) {
          totalCreated += data.data.created
          totalUpdated += data.data.updated

          for (const change of Array.from(pendingChanges.value.values())) {
            const existingIndex = translations.value.findIndex(
              t => t.translationKeyId === change.keyId && t.languageCode === change.languageCode
            )

            if (existingIndex >= 0) {
              translations.value[existingIndex].value = change.value
            }
          }
        } else {
          throw new Error(data.error || t('translation.batch.error'))
        }
      }

      pendingChanges.value.clear()
      showMessage(
        'success',
        t('translation.batch.success', { created: totalCreated, updated: totalUpdated })
      )
      await loadTranslations()
    } catch (error) {
      console.error('[TranslationManager] Erreur sauvegarde batch:', error)
      showMessage('error', t('translation.batch.error'))
    } finally {
      saveAllLoading.value = false
    }
  }

  function toggleKeyExpansion(keyId: number) {
    if (expandedKeys.value.has(keyId)) {
      expandedKeys.value.delete(keyId)
    } else {
      expandedKeys.value.add(keyId)
    }
  }

  async function exportTranslationsToLocales() {
    exportLoading.value = true
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'

      const languagesPromises = availableLanguages.value.map(async lang => {
        const response = await fetch(`${API_BASE_URL}/translations/${lang.code}`)
        const data = await response.json()

        if (data.success) {
          return {
            language: lang.code,
            translations: data.data,
          }
        }
        return null
      })

      const results = await Promise.all(languagesPromises)
      const validResults = results.filter(r => r !== null)

      validResults.forEach(result => {
        if (result) {
          const jsonContent = JSON.stringify(result.translations, null, 2)
          const blob = new Blob([jsonContent], { type: 'application/json' })
          const url = URL.createObjectURL(blob)

          const link = document.createElement('a')
          link.href = url
          link.download = `${result.language}.json`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        }
      })

      showMessage('success', t('translation.export.success', { count: validResults.length }))
    } catch (error) {
      console.error('[TranslationManager] Erreur export:', error)
      showMessage('error', t('translation.export.error'))
    } finally {
      exportLoading.value = false
    }
  }

  function resetFilters() {
    selectedLanguage.value = ''
    selectedContext.value = ''
    searchQuery.value = ''
    debouncedSearchQuery.value = ''
    currentPage.value = 1
  }

  function updateSearchQuery(query: string) {
    searchQuery.value = query
    currentPage.value = 1
    updateDebouncedSearch(query)
  }

  async function synchronizeWithFrontend() {
    syncLoading.value = true
    try {
      const frontendLocales = loadFrontendLocales()
      const allFrontendKeys = new Set<string>()
      Object.values(frontendLocales).forEach(locale => {
        Object.keys(locale).forEach(key => allFrontendKeys.add(key))
      })

      const existingBackendKeys = new Set(translationKeys.value.map(k => k.keyPath))
      const missingKeys = Array.from(allFrontendKeys).filter(key => !existingBackendKeys.has(key))

      syncStats.value = {
        frontendKeys: allFrontendKeys.size,
        backendKeys: existingBackendKeys.size,
        missingKeys: missingKeys.length,
      }

      if (missingKeys.length === 0) {
        showMessage('success', t('translation.sync.allPresent'))
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'
      const batchTranslations: Array<{ keyPath: string; languageCode: string; value: string }> = []

      for (const keyPath of missingKeys) {
        for (const lang of availableLanguages.value) {
          const value = frontendLocales[lang.code]?.[keyPath]
          if (value) {
            batchTranslations.push({
              keyPath,
              languageCode: lang.code,
              value,
            })
          }
        }
      }

      let createdTranslationsCount = 0
      let createdKeysCount = 0

      if (batchTranslations.length > 0) {
        const BATCH_SIZE = 100
        for (let i = 0; i < batchTranslations.length; i += BATCH_SIZE) {
          const batch = batchTranslations.slice(i, i + BATCH_SIZE)

          const batchResponse = await fetch(`${API_BASE_URL}/translations/batch`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ translations: batch }),
          })

          if (!batchResponse.ok) {
            let errorMessage = t('translation.sync.error')
            try {
              const errorData = await batchResponse.json()
              errorMessage =
                errorData?.error?.message || errorData?.error || errorData?.message || errorMessage
            } catch {
              errorMessage = `${errorMessage} (HTTP ${batchResponse.status})`
            }
            throw new Error(errorMessage)
          }

          const batchData = await batchResponse.json()

          if (batchData.success) {
            createdTranslationsCount += batchData.data.created + batchData.data.updated
          } else {
            throw new Error(batchData.error || t('translation.sync.error'))
          }
        }

        const uniqueKeys = new Set(batchTranslations.map(t => t.keyPath))
        createdKeysCount = uniqueKeys.size
      }

      await loadTranslations()

      syncStats.value = {
        frontendKeys: allFrontendKeys.size,
        backendKeys: translationKeys.value.length,
        missingKeys: Math.max(0, missingKeys.length - createdKeysCount),
      }

      showMessage(
        'success',
        t('translation.sync.success', {
          keys: createdKeysCount,
          translations: createdTranslationsCount,
        })
      )
    } catch (error) {
      console.error('[TranslationManager] Erreur synchronisation:', error)
      showMessage('error', t('translation.sync.error'))
    } finally {
      syncLoading.value = false
    }
  }

  async function analyzeSynchronization() {
    analyzeLoading.value = true
    try {
      const frontendLocales = loadFrontendLocales()
      const allFrontendKeys = new Set<string>()
      Object.values(frontendLocales).forEach(locale => {
        Object.keys(locale).forEach(key => allFrontendKeys.add(key))
      })

      const existingBackendKeys = new Set(translationKeys.value.map(k => k.keyPath))
      const missingKeys = Array.from(allFrontendKeys).filter(key => !existingBackendKeys.has(key))

      syncStats.value = {
        frontendKeys: allFrontendKeys.size,
        backendKeys: existingBackendKeys.size,
        missingKeys: missingKeys.length,
      }

      if (missingKeys.length === 0) {
        showMessage('success', t('translation.analyze.allPresent'))
      } else {
        showMessage('success', t('translation.analyze.missing', { count: missingKeys.length }))
      }
    } catch (error) {
      console.error('[TranslationManager] Erreur analyse:', error)
      showMessage('error', t('translation.analyze.error'))
    } finally {
      analyzeLoading.value = false
    }
  }

  // Import functions
  function openImportModal() {
    showImportModal.value = true
    importedData.value = null
    importLanguageCode.value = ''
    selectedForImport.value.clear()
    comparisonResults.value = null
  }

  function closeImportModal() {
    showImportModal.value = false
    importedData.value = null
    importLanguageCode.value = ''
    selectedForImport.value.clear()
    comparisonResults.value = null
  }

  async function handleFileImport(event: Event) {
    const target = event.target as HTMLInputElement
    const file = target.files?.[0]

    if (!file) {
      console.log('[TranslationManager] Aucun fichier sélectionné')
      return
    }

    console.log('[TranslationManager] Import fichier:', file.name, file.size, 'bytes')

    try {
      const text = await file.text()
      console.log('[TranslationManager] Fichier lu, taille:', text.length, 'caractères')
      const data = JSON.parse(text)
      console.log('[TranslationManager] JSON parsé, clés:', Object.keys(data).length)

      if (typeof data !== 'object' || Array.isArray(data)) {
        console.error(
          '[TranslationManager] Format JSON invalide:',
          typeof data,
          Array.isArray(data)
        )
        showMessage('error', t('translation.import.invalidFile'))
        return
      }

      importedData.value = data
      console.log('[TranslationManager] Données importées:', Object.keys(data).length, 'clés')

      // Si une langue est déjà sélectionnée, comparer immédiatement
      if (importLanguageCode.value) {
        console.log('[TranslationManager] Langue déjà sélectionnée, comparaison automatique')
        compareImportedData(data)
      } else {
        console.log('[TranslationManager] Aucune langue sélectionnée, attente de sélection')
      }
    } catch (error) {
      console.error('[TranslationManager] Erreur import fichier:', error)
      showMessage('error', t('translation.import.fileError'))
    }
  }

  function onImportLanguageChange(value: string) {
    importLanguageCode.value = value
    if (importedData.value && importLanguageCode.value) {
      compareImportedData(importedData.value)
    }
  }

  function compareImportedData(imported: Record<string, string>) {
    console.log('[TranslationManager] Début comparaison, langue:', importLanguageCode.value)
    console.log('[TranslationManager] Clés importées:', Object.keys(imported).length)

    if (!importLanguageCode.value) {
      console.error('[TranslationManager] Aucune langue sélectionnée pour la comparaison')
      showMessage('error', t('translation.import.selectLanguage'))
      return
    }

    const identical: Array<{ keyPath: string; value: string }> = []
    const different: Array<{ keyPath: string; currentValue: string; newValue: string }> = []
    const newKeys: Array<{ keyPath: string; value: string }> = []

    const currentTranslations = translations.value.filter(
      t => t.languageCode === importLanguageCode.value
    )
    console.log(
      '[TranslationManager] Traductions actuelles pour',
      importLanguageCode.value,
      ':',
      currentTranslations.length
    )

    const currentMap = new Map<string, string>()
    currentTranslations.forEach(t => {
      const key = translationKeys.value.find(k => k.id === t.translationKeyId)
      if (key) {
        currentMap.set(key.keyPath, t.value)
      }
    })
    console.log('[TranslationManager] Map des traductions actuelles:', currentMap.size, 'clés')

    for (const [keyPath, newValue] of Object.entries(imported)) {
      const currentValue = currentMap.get(keyPath)

      if (!currentValue) {
        newKeys.push({ keyPath, value: newValue })
      } else if (currentValue === newValue) {
        identical.push({ keyPath, value: newValue })
      } else {
        different.push({ keyPath, currentValue, newValue })
      }
    }

    console.log('[TranslationManager] Résultats comparaison:', {
      identical: identical.length,
      different: different.length,
      new: newKeys.length,
    })

    comparisonResults.value = {
      identical,
      different,
      new: newKeys,
    }

    selectedForImport.value.clear()
    different.forEach(item => selectedForImport.value.add(item.keyPath))
    newKeys.forEach(item => selectedForImport.value.add(item.keyPath))
    console.log(
      '[TranslationManager] Sélectionnées par défaut:',
      selectedForImport.value.size,
      'clés'
    )
  }

  function toggleImportSelection(keyPath: string) {
    if (selectedForImport.value.has(keyPath)) {
      selectedForImport.value.delete(keyPath)
    } else {
      selectedForImport.value.add(keyPath)
    }
  }

  function selectAllDifferent() {
    if (!comparisonResults.value) return
    comparisonResults.value.different.forEach(item => selectedForImport.value.add(item.keyPath))
  }

  function deselectAllDifferent() {
    if (!comparisonResults.value) return
    comparisonResults.value.different.forEach(item => selectedForImport.value.delete(item.keyPath))
  }

  function selectAllNew() {
    if (!comparisonResults.value) return
    comparisonResults.value.new.forEach(item => selectedForImport.value.add(item.keyPath))
  }

  function deselectAllNew() {
    if (!comparisonResults.value) return
    comparisonResults.value.new.forEach(item => selectedForImport.value.delete(item.keyPath))
  }

  async function importSelectedTranslations() {
    if (!importedData.value || !importLanguageCode.value || selectedForImport.value.size === 0) {
      return
    }

    importLoading.value = true
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1'

      const translationsToImport = Array.from(selectedForImport.value)
        .map(keyPath => {
          const value = importedData.value![keyPath]
          if (!value) return null
          return {
            keyPath,
            languageCode: importLanguageCode.value,
            value,
          }
        })
        .filter(t => t !== null) as Array<{ keyPath: string; languageCode: string; value: string }>

      const BATCH_SIZE = 100
      let totalCreated = 0
      let totalUpdated = 0

      for (let i = 0; i < translationsToImport.length; i += BATCH_SIZE) {
        const batch = translationsToImport.slice(i, i + BATCH_SIZE)

        const response = await fetch(`${API_BASE_URL}/translations/batch`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ translations: batch }),
        })

        const data = await response.json()

        if (data.success) {
          totalCreated += data.data.created
          totalUpdated += data.data.updated
        } else {
          throw new Error(data.error || t('translation.import.error'))
        }
      }

      showMessage(
        'success',
        t('translation.import.success', { created: totalCreated, updated: totalUpdated })
      )
      await loadTranslations()
      closeImportModal()
    } catch (error) {
      console.error('[TranslationManager] Erreur import:', error)
      showMessage('error', t('translation.import.error'))
    } finally {
      importLoading.value = false
    }
  }

  return {
    // State
    loading,
    exportLoading,
    syncLoading,
    analyzeLoading,
    saveAllLoading,
    importLoading,
    translationKeys,
    translations,
    expandedKeys,
    message,
    syncStats,
    pendingChanges,
    showImportModal,
    importedData,
    importLanguageCode,
    selectedForImport,
    comparisonResults,
    searchQuery,
    debouncedSearchQuery,
    selectedLanguage,
    selectedContext,
    currentPage,
    itemsPerPage,
    editableValues,
    availableLanguages,
    // Computed
    availableContexts,
    filteredKeys,
    totalPages,
    paginatedKeys,
    pendingChangesCount,
    selectedDifferentCount,
    selectedNewCount,
    // Methods
    loadTranslations,
    getTranslation,
    updateTranslation,
    hasUnsavedChanges,
    saveTranslation,
    saveAllPendingTranslations,
    toggleKeyExpansion,
    showMessage,
    exportTranslationsToLocales,
    resetFilters,
    updateSearchQuery,
    synchronizeWithFrontend,
    analyzeSynchronization,
    openImportModal,
    closeImportModal,
    handleFileImport,
    onImportLanguageChange,
    toggleImportSelection,
    selectAllDifferent,
    deselectAllDifferent,
    selectAllNew,
    deselectAllNew,
    importSelectedTranslations,
  }
}
